// Сетевой запрос за данными или скриптами
type Resource = {
    // Начало загрузки ресурса
    start: number;
    // Завершение загрузки ресурса
    end: number;
    // Ручка бекенда или ссылка на скрипт
    url: string;
    // Самый долгий родительский запрос
    longestParent: Resource | null;
    // Длительность запроса с учетом всех родительских запросов
    durationWithParents: number;
    // Цепочка long тасков, вызваных обработкой ответа на запрос
    responseLongTasks: number[];
};

// Периоды, когда even loop был заблокирован
type LongTask = {
    // Начало блокировки
    start: number;
    // Завершение блокировки
    end: number;
};

// Самая длинная цепочка запросов
type HighestWaterfall = {
    // Количество запросов в цепочке
    requestsCount: number;
    // Общая длительность цепочки запросов (ms)
    totalDuration: number;
    // Дебаг информация (урлы запросов + лонг таски между ними)
    attribution: Array<{ name: string; duration: number }>;
};

// Параметры настройки waterfall
export interface RequestWaterfallObserverOptions {
    // Список урлов, для которых все запросы не являются блокирующими
    // и не должны учитываться в waterfall.
    ignoreUrls?: RegExp[];
    // Список ключей query параметров, которые не считаются динамическими,
    // и по их значениям нужно агрегировать метрику.
    nonDynamicQueryKeys?: string[];
    // Метод проверки путей для которых происходит поиск и очистка динамических частей в пути,
    // чтобы по ним агрегировать данные.
    parseRoute?: (pathname: string) => string;
}

export type RequestWaterfallObserver = ReturnType<typeof createRequestWaterfallObserver>;

export function createRequestWaterfallObserver(options: RequestWaterfallObserverOptions = {}) {
    // Типы запросов, которые учитываем в waterfall
    const initiatorTypes = ['fetch', 'xmlhttprequest', 'script'];

    // Список урлов, для которых все запросы не являются блокирующими
    // и не должны учитываться в waterfall.
    const ignoreUrls = [
        // Логирование для баобаба, rum и error-counter
        /^https:\/\/yandex\.([a-z]+|com\.[a-z]+)\/clck\//,
        // Отправка данных в Яндекс Метрику
        /^https:\/\/mc\.yandex\.([a-z]+|com\.[a-z]+)\//,
        ...(options.ignoreUrls || []),
    ];

    // Список ключей query параметров, которые не считаются динамическими,
    // и по их значениям нужно агрегировать метрику.
    const nonDynamicQueryKeys = options.nonDynamicQueryKeys || [];

    // Время, в течении которого следующий запрос считается связанным с предыдущим запросом.
    const sequenceThreshold = 100; // ms

    // Список залогированных сетевых запросов
    const resources: Resource[] = [];

    // Список залогированных long тасков
    const longTasks: LongTask[] = [];

    // Последний ресурс в самой длинной цепочке запросов
    let longestWaterfall: Resource | null = null;

    function createObserver() {
        const observer = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                if (isResourceEntry(entry)) {
                    if (isValidResourceEntry(entry)) {
                        pushResource(entry);
                    }
                } else if (isLongTaskEntry(entry)) {
                    pushLongTask(entry);
                }
            }
        });

        return observer;
    }

    function isResourceEntry(entry: PerformanceEntry): entry is PerformanceResourceTiming {
        return entry.entryType === 'resource';
    }

    function isLongTaskEntry(entry: PerformanceEntry) {
        return entry.entryType === 'longtask';
    }

    function isValidResourceEntry(entry: PerformanceResourceTiming) {
        if (!initiatorTypes.includes(entry.initiatorType)) {
            return false;
        }

        // Если запрос попал в кеш, то достоверно нельзя определить waterfall,
        // потому что он попадет в цепочки всех запросов, которые были отправлены одновременно с ним
        const duration = entry.responseEnd - entry.fetchStart;
        if (duration < sequenceThreshold) {
            return false;
        }

        for (const re of ignoreUrls) {
            if (re.test(entry.name)) {
                return false;
            }
        }

        return true;
    }

    function pushResource(entry: PerformanceResourceTiming) {
        const resource: Resource = {
            start: entry.fetchStart,
            end: entry.responseEnd,
            url: entry.name,
            durationWithParents: entry.responseEnd - entry.fetchStart,
            longestParent: null,
            responseLongTasks: [],
        };
        adjustResourceToLongTasks(resource);
        logResource(resource);
    }

    function logResource(resource: Resource) {
        connectResources(resource);
        updateLongestWaterfall(resource);
        resources.push(resource);
    }

    function pushLongTask(entry: PerformanceEntry) {
        const longTask: LongTask = {
            start: entry.startTime,
            end: entry.startTime + entry.duration,
        };
        adjustResourcesToLongTask(longTask);
        longTasks.push(longTask);
    }

    function connectResources(nextResource: Resource) {
        for (let i = resources.length - 1; i >= 0; i--) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- тут всегда есть значение
            const resource = resources[i]!;

            if (nextResource.start - sequenceThreshold > resource.end) {
                // Нет смысла смотреть совсем старые записи
                //
                // Схематично:
                //   [a     a]
                //                >50ms
                //                        [b      b]
                break;
            } else if (nextResource.start < resource.end) {
                // Текущий запрос начался раньше, чем закончился предыдущий.
                // Это не может быть последовательными запросами.
                //
                // Схематично:
                //       [a    a]
                // [b             b]
                //
                // Или так:
                //   [a     a]
                //       [b      b]
                continue;
            } else if (nextResource.start - resource.end <= sequenceThreshold) {
                // Проверяем, что текущй запрос начался в течение 50ms от завершения
                // предыдущего запроса. Соединяем такие запросы в waterfall последовательность.
                //
                // Схематично:
                //   [a     a]
                //             <=50ms
                //                   [b      b]
                const durationWithParents = nextResource.longestParent?.durationWithParents ?? 0;
                if (durationWithParents < resource.durationWithParents) {
                    nextResource.longestParent = resource;

                    const duration = nextResource.end - nextResource.start;
                    nextResource.durationWithParents = resource.durationWithParents + duration;

                    updateLongestWaterfall(nextResource);
                }
            }
        }
    }

    function adjustResourcesToLongTask(longTask: LongTask) {
        for (let i = resources.length - 1; i >= 0; i--) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- тут всегда есть значение
            const resource = resources[i]!;

            if (longTask.start < resource.end && longTask.end > resource.end) {
                // Смещаем время завершения запроса на время окончания long таска, так как
                // фактическая обработка запроса в коде была заблокирована.
                //
                // Схематично:
                //   [a     a] --> a]
                //       [lt      lt]
                moveResourceEnd(resource, longTask.end);
                connectResources(resource);
            } else if (longTask.start - resource.end <= sequenceThreshold && longTask.end > resource.end) {
                // Смещаем время окончания запроса, если сразу после его завершения
                // был начат long таск, который мог помешать отправить следующий запрос
                // в течение 50ms.
                //
                // Схематично:
                //   [a     a]
                //             <=50ms
                //                    [lt      lt]
                const newResource: Resource = {
                    ...resource,
                    responseLongTasks: resource.responseLongTasks.concat(longTask.end - longTask.start),
                    durationWithParents: resource.durationWithParents + (longTask.end - resource.end),
                    end: longTask.end,
                };
                logResource(newResource);
            } else if (longTask.end < resource.start) {
                // Нет смысла смотреть совсем старые записи
                // Схематично:
                //             [a     a]
                //  [lt   lt]
                break;
            }
        }
    }

    function adjustResourceToLongTasks(resource: Resource) {
        for (let i = longTasks.length - 1; i >= 0; i--) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- тут всегда есть значение
            const longTask = longTasks[i]!;

            if (longTask.start < resource.end && longTask.end > resource.end) {
                // Смещаем время завершения запроса на время окончания long таска, так как
                // фактическая обработка запроса в коде была заблокирована.
                //
                // Схематично:
                // [a                   a]
                //         [lt         lt]
                //     [b        b] --> b]
                moveResourceEnd(resource, longTask.end);
            } else if (resource.start > longTask.end) {
                // Нет смысла смотреть совсем старые записи
                //
                // Схематично:
                //   [lt    lt]
                //                [b      b]
                break;
            }
        }
    }

    function moveResourceEnd(resource: Resource, target: number) {
        resource.durationWithParents += target - resource.end;
        resource.end = target;
        updateLongestWaterfall(resource);
    }

    function updateLongestWaterfall(resource: Resource) {
        if (!longestWaterfall) {
            longestWaterfall = resource;
            return;
        }

        if (resource.durationWithParents > longestWaterfall.durationWithParents) {
            longestWaterfall = resource;
        }
    }

    function isUrlValid(url: string): boolean {
        if (url) {
            if (!url.includes('.') || url.endsWith('.')) {
                return false;
            }
            try {
                // eslint-disable-next-line no-new -- Проверяется отсутствие ошибок, без других сайдэффектов
                new URL(url);
                return true;
            } catch (e) {
                // nothing
            }
        }
        return false;
    }

    function replaceDynamicPartsOfUrl(url: string) {
        if (!isUrlValid(url)) {
            return url;
        }

        const parsedUrl = new URL(url);
        const uniqueQueryKeys = new Set(parsedUrl.searchParams.keys());

        // Очищаем все значения query параметров, которые мы считаем динамическими,
        // чтобы уметь агрегировать такие запросы
        uniqueQueryKeys.forEach(queryKey => {
            const isDynamicQueryKey = !nonDynamicQueryKeys.includes(queryKey);

            if (isDynamicQueryKey) {
                parsedUrl.searchParams.set(queryKey, '');
            }
        });

        // Сортируем query параметры, чтобы уметь агрегировать запросы с разным порядком query
        parsedUrl.searchParams.sort();

        // Заменяем .tld для хостов ya.tld и yandex.tld на .ru,
        // чтобы уметь агрегировать запросы для разных корневых доменов
        parsedUrl.hostname = parsedUrl.hostname.replace(/(^|\.)(ya|yandex)\.([a-z]+|com\.[a-z]+)$/, '$1$2.ru');

        // Заменяем на все части пути, которые мы считаем динамическими
        if (options.parseRoute) {
            parsedUrl.pathname = options.parseRoute(parsedUrl.pathname);
        }

        return parsedUrl.toString();
    }

    function getHighestWaterfall(): HighestWaterfall {
        let requestsCount = 0;
        let totalDuration = 0;
        const attribution: HighestWaterfall['attribution'] = [];

        let resource = longestWaterfall;
        while (resource) {
            requestsCount++;

            if (requestsCount > 1) {
                for (const duration of resource.responseLongTasks) {
                    attribution.unshift({ name: 'Long Task', duration: Math.round(duration) });
                    totalDuration += duration;
                }
            }

            const duration = resource.end - resource.start;
            attribution.unshift({ name: replaceDynamicPartsOfUrl(resource.url), duration: Math.round(duration) });
            totalDuration += resource.end - resource.start;

            resource = resource.longestParent;
        }

        return {
            requestsCount,
            totalDuration: Math.round(totalDuration),
            attribution,
        };
    }

    let observer: PerformanceObserver | null = null;

    return {
        supported() {
            const supportedTypes = window.PerformanceObserver?.supportedEntryTypes ?? [];
            return supportedTypes.includes('resource') && supportedTypes.includes('longtask');
        },
        observe() {
            if (!this.supported()) {
                return;
            }

            observer = createObserver();
            observer.observe({ type: 'resource', buffered: true });
            observer.observe({ type: 'longtask', buffered: true });
        },
        disconnect() {
            observer?.disconnect();
            observer = null;
            this.reset();
        },
        getHighestWaterfall,
        getResources() {
            return resources;
        },
        reset() {
            resources.length = 0;
            longTasks.length = 0;
            longestWaterfall = null;
        },
    };
}
